import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { APP_VERSION } from './app-version';
import { AuthSessionService } from './auth-session.service';
import type { SavedPhoto } from './photo-upload.service';

declare const API_BASE_URL: string | undefined;
const MOBILE_TOKEN_KEY = 'nzp_mobile_token';
const MOBILE_USER_KEY = 'nzp_mobile_user';
const MOBILE_SESSION_MIGRATED_KEY = 'nzp_mobile_session_migrated';

export function showToast(type: 'success' | 'error', message: string, title?: string) {
  // Simple native fallback since mobile might not have a toast manager yet
  console.log(`[${type.toUpperCase()}] ${title || ''} ${message}`);
  // You can upgrade this to a visual toast later!
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  baseUrl = typeof API_BASE_URL !== 'undefined' ? API_BASE_URL : 'https://nzplumbinghq.co.nz/api/v1'; 

  currentUser = signal<any>(null); 
  currentShiftActive = signal(localStorage.getItem('nzp_shift_active') === '1');
  otpToken = signal<string>(sessionStorage.getItem('nzp_otp_token') || '');
  private readonly authSession = inject(AuthSessionService);
  readonly sessionStorageKey = MOBILE_TOKEN_KEY;

  constructor(private http: HttpClient) {
    this.migrateLegacySession();
    this.syncSessionFromStorage();
  }

  syncSessionFromStorage() {
    const storedUser = localStorage.getItem(MOBILE_USER_KEY);
    if (storedUser) {
      try { this.currentUser.set(JSON.parse(storedUser)); } catch {}
    } else {
      const user = this.userFromToken(this.accessToken());
      if (user) {
        localStorage.setItem(MOBILE_USER_KEY, JSON.stringify(user));
        this.currentUser.set(user);
      } else {
        this.currentUser.set(null);
      }
    }
  }

  public assetUrl(path: string | null | undefined): string {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path;
    const apiRoot = this.baseUrl.replace(/\/v1\/?$/, '');
    return `${apiRoot}${path.startsWith('/') ? path : `/${path}`}`;
  }

  private getHeaders(): HttpHeaders {
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const token = this.accessToken();
    if (token) {
      headers = headers.append('Authorization', `Bearer ${token}`);
    }
    return headers;
  }

  private getUploadHeaders(): HttpHeaders {
    let headers = new HttpHeaders();
    const token = this.accessToken();
    if (token) {
      headers = headers.append('Authorization', `Bearer ${token}`);
    }
    return headers;
  }

  isAuthenticated(): boolean {
    const token = this.accessToken();
    const user = this.userFromToken(token);
    if (!token || !user) return false;
    return true;
  }

  accessToken(): string {
    return localStorage.getItem(MOBILE_TOKEN_KEY) || '';
  }

  login(credentials: { email: string; password: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/login.php`, credentials).pipe(
      tap((res: any) => {
        const token = res?.data?.otp_token || res?.otp_token || '';
        if (res.success && token) {
           this.otpToken.set(token);
           sessionStorage.setItem('nzp_otp_token', token);
        }
      })
    );
  }

  verifyOtp(otp: string): Observable<any> {
    const payload = {
      otp_token: this.otpToken(),
      otp: otp
    };
    return this.http.post(`${this.baseUrl}/auth/verify_otp.php`, payload).pipe(
      tap((res: any) => {
        if (res.success && res.data?.token) {
           this.authSession.clear();
           this.storeSession(res.data.token, res.data.user);
           this.otpToken.set('');
           sessionStorage.removeItem('nzp_otp_token');
           this.pingAppDevice('login');
        }
      })
    );
  }

  pingAppDevice(eventType: 'login' | 'startup' | 'focus' | 'installed' | 'heartbeat' = 'heartbeat') {
    if (!this.isAuthenticated()) return;
    this.http.post(`${this.baseUrl}/app/device_ping.php`, this.appDevicePayload(eventType), { headers: this.getHeaders() }).subscribe({
      error: () => {}
    });
  }

  private appDevicePayload(eventType: string): any {
    const standalone = window.matchMedia('(display-mode: standalone)').matches
      || (navigator as Navigator & { standalone?: boolean }).standalone === true;
    const fullscreen = window.matchMedia('(display-mode: fullscreen)').matches;
    const minimalUi = window.matchMedia('(display-mode: minimal-ui)').matches;
    const displayMode = standalone ? 'standalone' : (fullscreen ? 'fullscreen' : (minimalUi ? 'minimal-ui' : 'browser'));
    return {
      event_type: eventType,
      device_id: this.getDeviceId(),
      app_context: 'mobile',
      platform: navigator.platform || '',
      user_agent: navigator.userAgent || '',
      display_mode: displayMode,
      is_installed: displayMode !== 'browser',
      app_version: APP_VERSION,
      service_worker_supported: 'serviceWorker' in navigator,
      push_supported: 'PushManager' in window
    };
  }

  private getDeviceId(): string {
    const key = 'nzp_mobile_device_id';
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const generated = typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `device_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(key, generated);
    return generated;
  }

  resendOtp(): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/resend_otp.php`, {
      otp_token: this.otpToken()
    }).pipe(
      tap((res: any) => {
        const token = res?.data?.otp_token || res?.otp_token || '';
        if (res.success && token) {
          this.otpToken.set(token);
          sessionStorage.setItem('nzp_otp_token', token);
        }
      })
    );
  }


  logout() {
    localStorage.removeItem(MOBILE_USER_KEY);
    localStorage.removeItem(MOBILE_TOKEN_KEY);
    localStorage.setItem(MOBILE_SESSION_MIGRATED_KEY, '1');
    localStorage.removeItem('nzp_shift_active');
    sessionStorage.removeItem('nzp_otp_token');
    this.currentUser.set(null);
    this.currentShiftActive.set(false);
    this.otpToken.set('');
    this.authSession.clear();
  }

  refreshSession(): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/refresh.php`, {}, { headers: this.getHeaders() }).pipe(
      tap((res: any) => {
        if (res?.success && res.data?.token) this.storeSession(res.data.token, res.data.user);
      })
    );
  }

  private storeSession(token: string, user: any) {
    localStorage.setItem(MOBILE_SESSION_MIGRATED_KEY, '1');
    if (user) {
      localStorage.setItem(MOBILE_USER_KEY, JSON.stringify(user));
      this.currentUser.set(user);
    }
    localStorage.setItem(MOBILE_TOKEN_KEY, token);
  }

  private migrateLegacySession() {
    if (localStorage.getItem(MOBILE_SESSION_MIGRATED_KEY) === '1') return;
    const legacyToken = localStorage.getItem('nzp_token');
    const legacyUser = localStorage.getItem('nzp_user');
    if (legacyToken) localStorage.setItem(MOBILE_TOKEN_KEY, legacyToken);
    if (legacyUser) localStorage.setItem(MOBILE_USER_KEY, legacyUser);
    localStorage.setItem(MOBILE_SESSION_MIGRATED_KEY, '1');
  }

  private userFromToken(token: string): any | null {
    if (!token) return null;
    try {
      const payload = token.split('.')[1];
      if (!payload) return null;
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = JSON.parse(atob(normalized.padEnd(normalized.length + (4 - normalized.length % 4) % 4, '=')));
      if (!decoded?.sub || (decoded.exp && decoded.exp * 1000 <= Date.now())) return null;
      return {
        id: decoded.sub,
        name: decoded.name || '',
        email: decoded.email || '',
        role: decoded.role || '',
        can_work_as_worker: Number(decoded.can_work_as_worker || 0),
        has_worker_access: !!decoded.has_worker_access
      };
    } catch {
      return null;
    }
  }

  getJobs(status?: string): Observable<any> {
    let url = `${this.baseUrl}/jobs/list.php`;
    if (status) {
      url += `?status=${encodeURIComponent(status)}`;
    }
    return this.http.get(url, { headers: this.getHeaders() });
  }

  getJob(id: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/jobs/get.php?id=${id}`, { headers: this.getHeaders() });
  }

  getQuoteContext(referenceId: string, modelType: string = 'Job', returnJobId: string = ''): Observable<any> {
    let url = `${this.baseUrl}/jobs/get_quote_context.php?job_id=${encodeURIComponent(referenceId)}&model_type=${encodeURIComponent(modelType)}`;
    if (returnJobId) {
      url += `&return_job_id=${encodeURIComponent(returnJobId)}`;
    }
    return this.http.get(url, { headers: this.getHeaders() });
  }

  updateJobStatus(payload: { id: string; status: string; pause_category?: string; pause_remarks?: string; pause_resolution_notes?: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/jobs/status.php`, payload, { headers: this.getHeaders() });
  }

  updateJobWorkSession(payload: { action: 'start'|'pause'|'resume'|'stop'; job_id: string; pause_category?: string; pause_remarks?: string; resume_remarks?: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/jobs/work_session.php`, payload, { headers: this.getHeaders() });
  }

  sendJobReviewRequest(jobId: string, customerEmail?: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/customer_feedback/send.php`, {
      reference_id: jobId,
      model_type: 'Job',
      customer_email: customerEmail?.trim() || undefined
    }, { headers: this.getHeaders() });
  }

  remindJobReviewLater(jobId: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/customer_feedback/remind_later.php`, { reference_id: jobId }, { headers: this.getHeaders() });
  }

  createJob(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/jobs/create.php`, payload, { headers: this.getHeaders() });
  }

  getFieldWorkers(): Observable<any> {
    return this.http.get(`${this.baseUrl}/users/field_workers.php`, { headers: this.getHeaders() });
  }

  searchCustomerByPhone(phone: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/customers/search.php?phone=${encodeURIComponent(phone)}`, { headers: this.getHeaders() });
  }

  addJobMaterial(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/jobs/add_material.php`, payload, { headers: this.getHeaders() });
  }

  updateJobMaterialReadiness(payload: { id: number | string; readiness_status: string; readiness_note?: string; required_by?: string | null }): Observable<any> {
    return this.http.post(`${this.baseUrl}/jobs/update_material_readiness.php`, payload, { headers: this.getHeaders() });
  }

  removeJobMaterial(id: number): Observable<any> {
    return this.http.post(`${this.baseUrl}/jobs/remove_material.php`, { id }, { headers: this.getHeaders() });
  }

  // --- QUOTES ---

  createQuote(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/quotes/create.php`, payload, { headers: this.getHeaders() });
  }

  generateQuotePdf(quoteId: number, payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/quotes/generate_pdf.php`, { quote_id: quoteId, ...payload }, { headers: this.getHeaders() });
  }

  // Photos
  getPhotoPage(referenceId: string, modelType: 'Job' | 'Project', beforeId: number | null = null) {
    let url = `${this.baseUrl}/jobs/photos.php?reference_id=${encodeURIComponent(referenceId)}&model_type=${modelType}&limit=24`;
    if (beforeId !== null) url += `&before_id=${beforeId}`;
    return this.http.get<{ success: boolean; message?: string; data?: {
      photos: SavedPhoto[]; pagination?: { total: number; has_more: boolean; next_before_id: number | null };
    } }>(url, { headers: this.getHeaders() });
  }
  uploadQueuedPhoto(referenceId: string, modelType: 'Job' | 'Project', photo: File, caption: string, uploadKey: string) {
    const formData = new FormData();
    formData.append('reference_id', referenceId);
    formData.append('model_type', modelType);
    formData.append('caption', caption);
    formData.append('upload_key', uploadKey);
    formData.append('photo', photo, photo.name);
    return this.http.post(`${this.baseUrl}/jobs/photos.php`, formData, {
      headers: this.getUploadHeaders(), observe: 'events', reportProgress: true
    });
  }
  getPhotos(jobId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/jobs/photos.php?reference_id=${jobId}&model_type=Job`, { headers: this.getHeaders() });
  }
  uploadJobPhotos(jobId: string, photos: File[], caption?: string): Observable<any> {
    const formData = new FormData();
    formData.append('reference_id', jobId);
    formData.append('model_type', 'Job');
    photos.forEach(photo => formData.append('photos[]', photo, photo.name));
    if (caption) formData.append('caption', caption);
    return this.http.post(`${this.baseUrl}/jobs/photos.php`, formData, { headers: this.getUploadHeaders() });
  }
  getProjectPhotos(projectId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/jobs/photos.php?reference_id=${encodeURIComponent(projectId)}&model_type=Project`, { headers: this.getHeaders() });
  }
  uploadProjectPhotos(projectId: string, photos: File[], comment?: string): Observable<any> {
    const formData = new FormData();
    formData.append('reference_id', projectId);
    formData.append('model_type', 'Project');
    photos.forEach(photo => formData.append('photos[]', photo, photo.name));
    if (comment) formData.append('caption', comment);
    return this.http.post(`${this.baseUrl}/jobs/photos.php`, formData, { headers: this.getUploadHeaders() });
  }
  downloadPhoto(photoId: number | string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/jobs/photo_download.php?id=${encodeURIComponent(photoId)}`, {
      headers: this.getHeaders(), responseType: 'blob'
    });
  }

  // Notes
  getNotes(jobId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/jobs/notes.php?reference_id=${jobId}&model_type=Job`, { headers: this.getHeaders() });
  }
  addNote(payload: { reference_id: string; model_type: string; content: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/jobs/notes.php`, payload, { headers: this.getHeaders() });
  }

  // Materials
  getMaterials(params: { search?: string, parent_id?: number, page?: number, limit?: number, include_children?: boolean } = {}): Observable<any> {
    let url = `${this.baseUrl}/materials/list.php`;
    const queryParams: string[] = [];
    if (params.search) queryParams.push(`search=${encodeURIComponent(params.search)}`);
    if (params.parent_id !== undefined) queryParams.push(`parent_id=${params.parent_id}`);
    if (params.limit !== undefined) queryParams.push(`limit=${params.limit}`);
    if (params.page !== undefined) queryParams.push(`page=${params.page}`);
    if (params.include_children !== undefined) queryParams.push(`include_children=${params.include_children ? '1' : '0'}`);
    if (queryParams.length > 0) url += `?${queryParams.join('&')}`;
    return this.http.get(url, { headers: this.getHeaders() });
  }

  // Purchase Orders
  getPurchaseOrders(params: any = {}): Observable<any> {
    let url = `${this.baseUrl}/purchase_orders/list.php`;
    const queryParams: string[] = [];
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        queryParams.push(`${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`);
      }
    });
    if (queryParams.length > 0) url += `?${queryParams.join('&')}`;
    return this.http.get(url, { headers: this.getHeaders() });
  }

  getPurchaseOrder(id: string | number): Observable<any> {
    return this.http.get(`${this.baseUrl}/purchase_orders/get.php?id=${encodeURIComponent(String(id))}`, { headers: this.getHeaders() });
  }

  savePurchaseOrder(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/purchase_orders/save.php`, payload, { headers: this.getHeaders() });
  }

  sendPurchaseOrder(id: string | number): Observable<any> {
    return this.http.post(`${this.baseUrl}/purchase_orders/send.php`, { purchase_order_id: id }, { headers: this.getHeaders() });
  }

  updatePurchaseOrderStock(id: string | number): Observable<any> {
    return this.http.post(`${this.baseUrl}/purchase_orders/update_stock.php`, { purchase_order_id: id }, { headers: this.getHeaders() });
  }

  getSuppliers(search: string = ''): Observable<any> {
    const query = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.http.get(`${this.baseUrl}/purchase_orders/suppliers.php${query}`, { headers: this.getHeaders() });
  }

  getMyFleetVehicle(): Observable<any> {
    return this.http.get(`${this.baseUrl}/fleet/my_vehicle.php`, { headers: this.getHeaders() });
  }

  updateMyFleetOdometer(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/fleet/my_odometer.php`, payload, { headers: this.getHeaders() });
  }

  // Leave Requests
  getMyLeaveRequests(): Observable<any> {
    return this.http.get(`${this.baseUrl}/leave_requests/my.php`, { headers: this.getHeaders() });
  }

  getMyLeaveRequest(id: string | number): Observable<any> {
    return this.http.get(`${this.baseUrl}/leave_requests/my.php?id=${encodeURIComponent(id)}`, { headers: this.getHeaders() });
  }

  submitLeaveRequest(payload: any): Observable<any> {
    if (payload instanceof FormData) {
      return this.http.post(`${this.baseUrl}/leave_requests/submit.php`, payload, { headers: this.getUploadHeaders() });
    }
    return this.http.post(`${this.baseUrl}/leave_requests/submit.php`, payload, { headers: this.getHeaders() });
  }

  cancelLeaveRequest(id: string | number): Observable<any> {
    return this.http.post(`${this.baseUrl}/leave_requests/cancel.php`, { id }, { headers: this.getHeaders() });
  }

  // --- AUTH ACTIVATION & RESET ---

  verifyActivateToken(token: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/auth/activate_verify.php?token=${token}`);
  }

  submitActivate(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/activate_submit.php`, payload);
  }

  forgotPassword(email: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/forgot_password.php`, { email });
  }

  submitResetPassword(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/reset_password.php`, payload);
  }

  changePassword(payload: { current_password: string; new_password: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/change_password.php`, payload, { headers: this.getHeaders() });
  }

  // --- PROJECTS ---
  getProjects(): Observable<any> {
    return this.http.get(`${this.baseUrl}/projects/list.php`, { headers: this.getHeaders() });
  }

  getProject(id: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/projects/get.php?id=${id}`, { headers: this.getHeaders() });
  }

  getProjectWorkSessions(projectId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/projects/work_session.php?project_id=${encodeURIComponent(projectId)}`, { headers: this.getHeaders() });
  }

  workSession(payload: { action: 'start'|'pause'|'resume'|'stop'; project_id: string; notes?: string }): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/projects/work_session.php`, payload, { headers: this.getHeaders() }).pipe(
      tap(res => {
        if (res?.success && typeof window !== 'undefined') window.dispatchEvent(new Event('nzp-project-session-changed'));
      })
    );
  }

  saveProjectPhaseForm(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/projects/save_phase_form.php`, payload, { headers: this.getHeaders() });
  }

  updateProjectPhase(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/projects/phases.php`, payload, { headers: this.getHeaders() });
  }

  getCompanySettings(): Observable<any> {
    return this.http.get(`${this.baseUrl}/settings/get.php`, { headers: this.getHeaders() });
  }

  updateCompanySettings(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/settings/update.php`, data, { headers: this.getHeaders() });
  }

  // --- AS BUILT ---
  getAsBuiltJobs(): Observable<any> {
    return this.http.get(`${this.baseUrl}/ai/list_drainage_jobs.php`, { headers: this.getHeaders() });
  }

  createManualAsBuiltPlan(payload: { title: string; drawing_width_metres?: number; reference_id?: string; model_type?: 'Project' | 'Job' }): Observable<any> {
    return this.http.post(`${this.baseUrl}/ai/create_manual_drainage_job.php`, payload, { headers: this.getHeaders() });
  }

  getAsBuiltJob(jobId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/ai/status_drainage.php?job_id=${encodeURIComponent(jobId)}`, { headers: this.getHeaders() });
  }

  saveAsBuiltJob(jobId: string, editorState: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/ai/save_drainage_job.php`, { job_id: jobId, editor_state: editorState }, { headers: this.getHeaders() });
  }

  generateAsBuiltPdf(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/ai/generate_as_built_pdf.php`, payload, { headers: this.getHeaders() });
  }

  uploadAsBuiltPlan(file: File, payload: { title: string; extraction_mode: 'full' | 'boundary_only'; reference_id?: string; model_type?: 'Project' | 'Job' }): Observable<any> {
    const formData = new FormData();
    formData.append('plan_image', file);
    formData.append('title', payload.title);
    formData.append('extraction_mode', payload.extraction_mode);
    if (payload.reference_id) formData.append('reference_id', payload.reference_id);
    if (payload.model_type) formData.append('model_type', payload.model_type);

    let headers = new HttpHeaders();
    const token = this.accessToken();
    if (token) headers = headers.append('Authorization', `Bearer ${token}`);
    return this.http.post(`${this.baseUrl}/ai/extract_drainage.php`, formData, { headers });
  }

  attachAsBuiltJob(jobId: string, modelType: 'Project' | 'Job', referenceId: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/ai/attach_drainage_job.php`, {
      job_id: jobId,
      model_type: modelType,
      reference_id: referenceId
    }, { headers: this.getHeaders() });
  }

  deleteAsBuiltJob(jobId: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/ai/delete_drainage_job.php`, { job_id: jobId }, { headers: this.getHeaders() });
  }

  asBuiltAssetUrl(path: string): string {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path;
    const apiRoot = this.baseUrl.replace(/\/v1\/?$/, '');
    return `${apiRoot}${path.startsWith('/') ? path : `/${path}`}`;
  }

  // --- SHIFTS ---
  getShiftStatus(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/shifts/status.php`, { headers: this.getHeaders() }).pipe(
      tap(res => {
        if (res?.success && res?.data) this.applyShiftSession(res.data);
      })
    );
  }

  updateShift(action: 'start' | 'end', location?: any): Observable<any> {
    const previousState = this.currentShiftActive();
    this.setCurrentShiftActive(action === 'start');
    return this.http.post<any>(`${this.baseUrl}/shifts/status.php`, { action, ...(location || {}) }, { headers: this.getHeaders() }).pipe(
      tap({
        next: res => {
          if (res?.success && res?.data) this.applyShiftSession(res.data);
          else this.setCurrentShiftActive(previousState);
        },
        error: () => this.setCurrentShiftActive(previousState)
      })
    );
  }

  private setCurrentShiftActive(active: boolean) {
    this.currentShiftActive.set(active);
    localStorage.setItem('nzp_shift_active', active ? '1' : '0');
  }

  private applyShiftSession(data: any) {
    this.setCurrentShiftActive(!!data.active);
    if (data.session_token) {
      localStorage.setItem(MOBILE_TOKEN_KEY, String(data.session_token));
    }
  }

  stopBlockingProjectSession(sessionId: number): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/shifts/status.php`, {
      action: 'stop_project_session',
      session_id: sessionId
    }, { headers: this.getHeaders() }).pipe(
      tap(res => {
        if (res?.success && res?.data) this.applyShiftSession(res.data);
        if (res?.success && typeof window !== 'undefined') window.dispatchEvent(new Event('nzp-project-session-changed'));
      })
    );
  }

  updateLocation(location: { latitude: number; longitude: number; accuracy_m?: number | null }): Observable<any> {
    return this.http.post(`${this.baseUrl}/locations/update.php`, location, { headers: this.getHeaders() });
  }

  // --- NOTIFICATIONS ---
  getNotifications(params: { limit?: number; unread_only?: boolean; status?: string; priority?: string; type?: string; reference_type?: string } = {}): Observable<any> {
    const queryParams: string[] = [];
    Object.keys(params).forEach(key => {
      const value = (params as any)[key];
      if (value !== undefined && value !== null && value !== '') {
        queryParams.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value === true ? 1 : value))}`);
      }
    });
    const query = queryParams.length ? `?${queryParams.join('&')}` : '';
    return this.http.get(`${this.baseUrl}/notifications/list.php${query}`, { headers: this.getHeaders() });
  }

  markNotificationRead(id: number | string): Observable<any> {
    return this.http.post(`${this.baseUrl}/notifications/mark_read.php`, { id }, { headers: this.getHeaders() });
  }

  markAllNotificationsRead(): Observable<any> {
    return this.http.post(`${this.baseUrl}/notifications/mark_read.php`, { all: true }, { headers: this.getHeaders() });
  }

  acceptSafetyAcknowledgement(payload: {
    reference_id: string;
    model_type: 'Job' | 'Project';
    statement: string;
    status?: 'Accepted' | 'Declined';
    decline_reason?: string;
    decline_photo_data?: string | null;
  }): Observable<any> {
    return this.http.post(`${this.baseUrl}/safety/acknowledge.php`, payload, { headers: this.getHeaders() });
  }
}
